import { Component, Prop, h, Watch, Element, State } from '@stencil/core';
import { ChartData, ChartTooltipItem, ChartOptions } from 'chart.js';
import { GRAPH_COLOR_MAIN, GRAPH_COLOR_EXTRA } from '../../lib/graph/constants';
import { getDataPoints, getSegments } from '../../../utils/utils';
import { RuntimeModel, RuntimeDisplay } from '../types';
import { Fragment } from '../../functional/Fragment';
import { LayoutSize, Maybe } from '../../../global/types';
import { round } from '../../../utils/math';
import { getFormatter, formatTime } from '../../../utils/formatting';
import { getLayoutClass } from '../../../global/constants';
import { runtime, getLoadStartPoint } from '../../../utils/equation';
import { TableRow } from '../../lib/print/print';
import { combineInAscSortOrder, inRange, equals } from '../../../utils/array';

@Component({
  tag: 'rt-runtime',
  shadow: true,
  styleUrl: 'runtime.scss',
})
export class RuntimeComponent {
  @Prop() model: RuntimeModel;

  @Prop() display: RuntimeDisplay = 'default';

  @Prop() locale;

  @Element() el: HTMLRtRuntimeElement;

  private formatLoad: (power: number, withScale?: boolean) => string;
  private useKW = false;
  private graphEl: HTMLRtGraphElement;
  @State() layout: Maybe<LayoutSize>;

  @State() xAsisLabels: number[] = [];

  @Watch('xAsisLabels')
  handleXAxisLabelsChange() {
    this.graphEl.updateChartData(this.chartData());
  }

  private getScaleValue = (): string => {
    const { power } = this.model.translations;
    return this.useKW ? power.abbr.kWatts : power.abbr.watts;
  };

  @Watch('model')
  onModelChange(model) {
    this.useKW = model.product.pwrRated >= 1e4;
    this.formatLoad = (power: number, withScale = true) => {
      if (this.useKW) {
        return `${this.formatter.format(round(power / 1e3))}${
          withScale ? ` ${this.getScaleValue()}` : ''
        }`;
      }
      return `${this.formatter.format(round(power))}${
        withScale ? ` ${this.getScaleValue()}` : ''
      }`;
    };
  }

  private formatter;

  @Watch('locale')
  changeFormatter() {
    this.formatter = getFormatter(this.locale);
  }

  connectedCallback() {
    this.changeFormatter();
  }

  componentWillLoad() {
    this.onModelChange(this.model);
  }

  private static getRuntime = runtime;

  getTableRows(loads: number[]) {
    const { product, bundles, translations } = this.model;
    const productRow = {
      render: product.url ? (
        <se-link url={product.url}>{product.id}</se-link>
      ) : (
        <span class="row-1">{product.id}</span>
      ),
      labels: loads.map((load) => ({
        label: this.formatLoad(load),
        value: formatTime(
          RuntimeComponent.getRuntime(product)(load),
          translations.time.abbr,
        ),
      })),
    };
    const bundlesRows = bundles.map((bundle) => {
      const bundleRow = {
        render: (
          <span class="bundle-info">
            <rt-tag content={'1x'} />
            {product.url ? (
              <se-link url={product.url}>{product.id}</se-link>
            ) : (
              <span class="row-1">{product.id}</span>
            )}
            <span class="plus-symbol">+</span>
            <rt-tag content={`${bundle.quantity}x`} />
            {bundle.url ? (
              <se-link url={bundle.url}>{bundle.id}</se-link>
            ) : (
              <span class="row-1">{bundle.id}</span>
            )}
          </span>
        ),
        labels: loads.map((load) => ({
          label: this.formatLoad(load),
          value: formatTime(
            RuntimeComponent.getRuntime({ ...product, vah: bundle.vah })(load),
            translations.time.abbr,
          ),
        })),
      };
      return bundleRow;
    });
    return [productRow, ...bundlesRows];
  }

  getChartConfig() {
    const { translations, bundles } = this.model;

    const calculateAlignment = () => {
      if (bundles.length > 2 && this.layout === LayoutSize.SMALL) {
        return 'start';
      }
      return 'center';
    };

    const config: ChartOptions = {
      scales: {
        xAxes: [
          {
            scaleLabel: {
              labelString: `${
                translations.xAxisLabel
              } (${this.getScaleValue()})`,
            },
            ticks: {
              callback: (value: string) => {
                return this.formatLoad(+value, false);
              },
            },
            afterBuildTicks: (_, ticks) => {
              // if statement below fixes chartjs issue when all lines are hidden
              if (ticks[ticks.length - 1] === 1) {
                return [];
              }

              if (!equals(ticks, this.xAsisLabels)) {
                this.xAsisLabels = ticks;
              }
              return ticks;
            },
          },
        ],
        yAxes: [
          {
            type: 'logarithmic',
            ticks: {
              min: 0,
              max: 'auto',
              callback: (value: number) => {
                return this.formatter.format(value);
              },
            },
            scaleLabel: {
              labelString: `${this.model.translations.yAxisLabel} (${translations.time.abbr.min})`,
            },
          },
        ],
      },
      tooltips: {
        displayColors: true,
        callbacks: {
          title: ([item]) => {
            const load = item !== undefined ? this.formatLoad(+item.xLabel) : '';
            return [translations.tooltipTitle, load];
          },
          label: (tooltipItem: ChartTooltipItem, data: ChartData) => {
            return [
              data.datasets[tooltipItem.datasetIndex].label,
              formatTime(+tooltipItem.yLabel, translations.time.abbr),
            ];
          },
        },
      },
      legend: {
        display: this.model.bundles.length > 0,
        align: calculateAlignment(),
      },
    };
    return config;
  }

  chartData() {
    const { product } = this.model;
    const runtimeFunction = RuntimeComponent.getRuntime(product);
    const initialDataPoints = getDataPoints(
      getLoadStartPoint(this.model),
      product.pwrRated,
      180,
    );
    const labels = combineInAscSortOrder(
      initialDataPoints,
      this.xAsisLabels.filter(
        inRange(
          initialDataPoints[0],
          initialDataPoints[initialDataPoints.length - 1],
        ),
      ),
    );

    return {
      datasets: [
        {
          label: `${this.model.product.id}`,
          borderColor: GRAPH_COLOR_MAIN,
          backgroundColor: GRAPH_COLOR_MAIN,
          borderWidth: 2,
          data: labels.map((x) => ({ x, y: runtimeFunction(x) })),
          showLine: true,
          fill: false,
        },
        ...this.model.bundles.map((bundle, index) => ({
          label: `${this.model.product.id} + ${bundle.id} (${bundle.quantity})`,
          borderColor: GRAPH_COLOR_EXTRA[index % GRAPH_COLOR_EXTRA.length],
          backgroundColor: GRAPH_COLOR_EXTRA[index % GRAPH_COLOR_EXTRA.length],
          borderWidth: 2,
          data: labels.map((x) => ({
            x,
            y: RuntimeComponent.getRuntime({
              ...this.model.product,
              vah: bundle.vah,
            })(x),
          })),
          fill: false,
          showLine: true,
        })),
      ],
    };
  }

  renderGraph() {
    return (
      <rt-graph
        ref={(el) => (this.graphEl = el)}
        options={this.getChartConfig()}
        data={this.chartData()}
        type={'scatter'}
        size={{ height: this.model.bundles.length > 3 ? '500px' : '350px' }}
      ></rt-graph>
    );
  }

  renderInfo() {
    const {
      translations: { info },
    } = this.model;
    if (info) {
      return (
        <rt-info class={getLayoutClass(this.layout)}>
          <span slot="info">{info}</span>
          {this.printLink()}
        </rt-info>
      );
    }
    return this.printLink();
  }

  getTableLabels() {
    return getSegments(
      getLoadStartPoint(this.model),
      this.model.product.pwrRated,
      10,
    );
  }

  renderTable() {
    const tableLabels = this.getTableLabels();
    return (
      <rt-table
        columns={tableLabels.map((load) => this.formatLoad(load, true))}
        rows={this.getTableRows(tableLabels)}
        columnLabelTitle={this.model.translations.xAxisLabel}
        columnValueTitle={this.model.translations.yAxisLabel}
        class={getLayoutClass(this.layout)}
      ></rt-table>
    );
  }

  printLink() {
    const { product, bundles, translations } = this.model;

    const tableData = () => {
      const labels = this.getTableLabels();

      return {
        columns: labels.map((load) => this.formatLoad(load, true)),
        rows: [
          [
            [
              {
                id: product.id,
                url: product.url,
              },
            ],
            ...labels.map((load) => formatTime(
              RuntimeComponent.getRuntime(product)(load),
              translations.time.abbr,
            )),
          ],
          ...bundles.map((bundle) => {
            return [
              [
                {
                  id: product.id,
                  url: product.url,
                  quantity: 1,
                },
                bundle,
              ],
              ...labels.map((load) => {
                return formatTime(
                  RuntimeComponent.getRuntime({
                    ...product,
                    vah: bundle.vah,
                  })(load),
                  translations.time.abbr,
                );
              }),
            ];
          }),
        ] as TableRow[],
        title: `${translations.xAxisLabel} / ${translations.yAxisLabel}`,
      };
    };

    return (
      <rt-print
        data={{
          linkText: translations.printLabel,
          type: translations.yAxisLabel,
          id: product.id,
          name: product.name,
          family: product.family,
          graphEl: () => this.graphEl,
          tableContent: this.display === 'graph' ? null : tableData(),
          infoContent: translations.info,
        }}
      ></rt-print>
    );
  }

  renderAll() {
    switch (this.display) {
      case 'graph': {
        return (
          <Fragment>
            {this.renderGraph()}
            {this.renderInfo()}
          </Fragment>
        );
      }
      case 'table': {
        return (
          <Fragment>
            {this.renderTable()}
            {this.printLink()}
          </Fragment>
        );
      }
      default: {
        return (
          <Fragment>
            {this.renderGraph()}
            {this.renderTable()}
            {this.renderInfo()}
          </Fragment>
        );
      }
    }
  }

  handleLayoutChange(layout: LayoutSize): void {
    this.layout = layout;
    if (this.graphEl) {
      this.graphEl.updateChartOptions(this.getChartConfig());
    }
  }

  render() {
    return (
      <rt-resize-watcher
        onBreakPointChanged={(e: CustomEvent) => {
          this.handleLayoutChange(e.detail);
        }}
      >
        {this.renderAll()}
      </rt-resize-watcher>
    );
  }
}
